import { useId } from "react";

import { cn } from "../lib/cn";

export interface ToggleProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  checked: boolean;
  /** Optional for a display-only switch. */
  onCheckedChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  /** Addresses the switch itself, so an external `<label htmlFor>` finds it. */
  id?: string;
  className?: string;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * OFF uses `annotation`, not Checkbox's `border-ink`: ink (14.5:1) would draw
 * louder than ON's green (3.95:1). Non-`id` native props land on the wrapper.
 */
export function Toggle({
  checked,
  onCheckedChange,
  label,
  disabled = false,
  id,
  className,
  ...rest
}: ToggleProps) {
  const generatedId = useId();
  const switchId = id ?? generatedId;
  const labelId = `${switchId}-label`;

  return (
    <span
      {...rest}
      className={cn("inline-flex items-center gap-2.5", className)}
    >
      <button
        type="button"
        role="switch"
        id={switchId}
        aria-checked={checked}
        aria-labelledby={label ? labelId : undefined}
        disabled={disabled}
        onClick={() => onCheckedChange?.(!checked)}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border",
          "transition duration-150 ease-paper motion-reduce:transition-none",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "disabled:cursor-not-allowed",
          checked
            ? "border-ink-working bg-ink-working"
            : "border-annotation bg-recess",
          disabled &&
            (checked ? "border-hollow bg-hollow" : "border-rule bg-recess"),
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "size-4 rounded-full border border-transparent bg-card shadow-lift",
            "transition-transform duration-150 ease-paper motion-reduce:transition-none",
            checked ? "translate-x-[16px]" : "translate-x-[2px]",
            !checked && !disabled && "border-annotation",
            disabled && "shadow-none",
          )}
        />
      </button>
      {label && (
        <span
          id={labelId}
          onClick={disabled ? undefined : () => onCheckedChange?.(!checked)}
          className={cn(
            "font-body text-sm",
            disabled ? "text-faint" : "cursor-pointer text-ink-body",
          )}
        >
          {label}
        </span>
      )}
    </span>
  );
}
